import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getApiErrorMessage } from "../../../services/apiErrors";
import {
  useFetchTenantWorkspaces,
  useSelectTenant,
} from "../../../hooks/useTenantContext";
import type {
  MembershipRead,
  TenantWorkspaceRead,
} from "../../../services/tenantContext/tenantContext.types";

export interface UseTenantSelectInternalStateArgs {
  tenants: MembershipRead[];
  /** Tenant preselected on mount; the user can still change it. */
  initialTenantId?: string | null;
  /** Receives the chosen tenant AND the workspace resolved for it. */
  onContinue: (args: {
    tenant: MembershipRead;
    workspaceId: string;
    workspaces: TenantWorkspaceRead[];
  }) => void;
  /**
   * Called instead of setting `continueError` when the workspace lookup 401s.
   * An expired token is not a per-tenant problem, so it must not render as an
   * inline message next to the dropdown — the page signs the user out.
   */
  onUnauthorized?: () => void;
  /**
   * Login picker only: when the account has exactly one organization there is
   * nothing to choose, so select it and continue once, as if the user clicked
   * Continue. Off by default — the in-app org switcher must keep asking.
   */
  autoContinueSingleTenant?: boolean;
  /** The membership list is still loading (an empty `tenants` is not final yet). */
  tenantsLoading?: boolean;
}

export interface UseTenantSelectInternalState {
  selectedTenantId: string | null;
  selectTenant: (tenantId: string) => void;
  /** False until a tenant is chosen, and while the continue call is running. */
  canContinue: boolean;
  isContinuing: boolean;
  /** Failure from the workspace lookup — the tenant list has its own error path. */
  continueError: string | null;
  handleContinue: () => Promise<void>;
  /**
   * Auto-continue is on and has not given up: the list is still loading, or it
   * holds one organization that is being opened. The picker has nothing to
   * show the user then, only a loading state. False again after a failure.
   */
  isAutoContinuing: boolean;
}

/**
 * Internal UI state for the tenant-selection step, extracted per CLAUDE.md so
 * the organism stays a lean compositor.
 *
 * Owns the step's API calls: once a tenant is picked, "Continue" first mints
 * a token scoped to that tenant (`select-tenant`), then fetches the tenant's
 * workspaces and takes the first one. The user never sees or picks a
 * workspace — the app just needs *a* valid one to scope `/home`.
 *
 * Minting the token before the workspace fetch means every request from this
 * point on — including that fetch — is scoped to the tenant the user just
 * picked, not whichever tenant login happened to default to.
 *
 * The workspace fetch goes through `useFetchTenantWorkspaces`, which writes
 * the same React Query cache entry `TenantWorkspaceGuard` reads, so the guard
 * on the destination route resolves without a second request.
 */
export const useTenantSelectInternalState = ({
  tenants,
  initialTenantId = null,
  onContinue,
  onUnauthorized,
  autoContinueSingleTenant = false,
  tenantsLoading = false,
}: UseTenantSelectInternalStateArgs): UseTenantSelectInternalState => {
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    initialTenantId,
  );
  const [isContinuing, setIsContinuing] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);
  const fetchWorkspacesForTenant = useFetchTenantWorkspaces();
  const selectTenantToken = useSelectTenant();
  // A ref, not just the `isContinuing` state, guards re-entry: two clicks
  // dispatched before React has re-rendered would both still read the old
  // (false) state value — the ref is written synchronously, so the second
  // click always sees the first click's claim.
  const isContinuingRef = useRef(false);

  const selectTenant = useCallback((tenantId: string) => {
    // Picking a row now closes the dropdown (TenantSelectDropdown), so there
    // is no interaction path left for "uncheck to clear" — always set, never
    // toggle to null.
    setSelectedTenantId(tenantId);
    // Clear a previous failure so the old message doesn't hang over a fresh
    // choice — the user has changed the thing that failed.
    setContinueError(null);
  }, []);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === selectedTenantId) ?? null,
    [tenants, selectedTenantId],
  );

  const handleContinue = useCallback(async () => {
    // Guard re-entry: a double-click must not fire two select-tenant/workspace
    // lookups.
    if (!selectedTenant || isContinuingRef.current) return;
    isContinuingRef.current = true;

    setIsContinuing(true);
    setContinueError(null);

    try {
      const { access_token: newAccessToken } = await selectTenantToken(
        selectedTenant.tenant_id,
      );
      // Overwrite before the workspace fetch below so it (and everything
      // after) runs under the tenant the user just picked, not the token's
      // previous scope.
      localStorage.setItem("accessToken", newAccessToken);

      const workspaces = await fetchWorkspacesForTenant(
        selectedTenant.tenant_id,
      );
      const workspaceId = workspaces[0]?.id;

      if (!workspaceId) {
        // Navigating without a workspace id is what makes getActiveWorkspaceId()
        // throw later, deep inside the app — stop here instead.
        setContinueError("This organization has no workspaces yet.");
        return;
      }

      onContinue({ tenant: selectedTenant, workspaceId, workspaces });
    } catch (error) {
      if ((error as { status?: number })?.status === 401 && onUnauthorized) {
        onUnauthorized();
        return;
      }
      setContinueError(
        getApiErrorMessage(error, "Couldn't open this tenant. Please try again."),
      );
    } finally {
      isContinuingRef.current = false;
      setIsContinuing(false);
    }
  }, [
    selectedTenant,
    onContinue,
    onUnauthorized,
    selectTenantToken,
    fetchWorkspacesForTenant,
  ]);

  // One shot: a failed auto-continue leaves the normal picker + inline error,
  // and a retry is the user's own Continue click — never a second automatic try.
  const hasAutoContinuedRef = useRef(false);
  const singleTenantId = tenants.length === 1 ? tenants[0].tenant_id : null;
  useEffect(() => {
    if (!autoContinueSingleTenant || !singleTenantId) return;
    if (hasAutoContinuedRef.current) return;
    // Wait for the selection to land so handleContinue sees the tenant.
    if (selectedTenantId !== singleTenantId) {
      setSelectedTenantId(singleTenantId);
      return;
    }
    hasAutoContinuedRef.current = true;
    void handleContinue();
  }, [autoContinueSingleTenant, singleTenantId, selectedTenantId, handleContinue]);

  return {
    selectedTenantId,
    selectTenant,
    canContinue: selectedTenant !== null && !isContinuing,
    isContinuing,
    continueError,
    handleContinue,
    isAutoContinuing:
      autoContinueSingleTenant &&
      continueError === null &&
      (tenantsLoading || singleTenantId !== null),
  };
};
